// ============================================
// ConverseTech Frontend - Session Page
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { startSession, endSession, getCurrentUser } from '../lib/api';
import { useSessionStore } from '../stores/sessionStore';
import {
  ModeSelector,
  TranscriptPanel,
  FeedbackPanel,
  SessionTimer,
  ConsentModal,
  MicControl,
  PhaseProgress,
  DETPhaseControl,
} from '../components';
import FunctionCallOverlay from '../components/FunctionCallOverlay';
import { useAudioCapture, useGeminiLive, useWebSocket, useDETSimulation } from '../hooks';
import { AudioStreamPlayer } from '../lib/audio-stream-player';
import type { TrainingMode, WSServerMessage, TranscriptUpdate, GeminiFunctionCall, DETPhaseConfig, DETPhase } from '@conversetech/shared';
import { TRAINING_MODE_LABELS, METRICS_CONFIG, getPromptForMode } from '@conversetech/shared';

export default function Session() {
  const navigate = useNavigate();
  const [showConsent, setShowConsent] = useState(false);
  const [pendingMode, setPendingMode] = useState<TrainingMode | null>(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [geminiError, setGeminiError] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [activeFunctionCall, setActiveFunctionCall] = useState<GeminiFunctionCall | null>(null);
  const audioPlayerRef = useRef<AudioStreamPlayer | null>(null);
  const detIsOverTimeRef = useRef(false);
  const prevIsSpeakingRef = useRef(false);
  const [geminiHasSpoken, setGeminiHasSpoken] = useState(false);
  const [geminiSpokeThisPhase, setGeminiSpokeThisPhase] = useState(false);
  const [waitingForSilence, setWaitingForSilence] = useState(false);

  const {
    sessionId,
    wsUrl,
    mode,
    state,
    isActive,
    transcript,
    setSession,
    setState,
    setActive,
    addTranscript,
    setMetrics,
    clearSession,
    setDETPhase,
    addDETScore,
    resetDETState,
  } = useSessionStore();

  // Handle messages from worker WebSocket
  const handleWorkerMessage = useCallback((message: WSServerMessage) => {
    switch (message.type) {
      case 'STATE_CHANGED': {
        const statePayload = message.payload as { state: string };
        setState(statePayload.state as Parameters<typeof setState>[0]);
        break;
      }
      case 'METRICS_UPDATE': {
        const metricsPayload = message.payload as { metrics: Parameters<typeof setMetrics>[0] };
        setMetrics(metricsPayload.metrics);
        break;
      }
      case 'SESSION_ENDED':
        break;
    }
  }, [setState, setMetrics]);

  // Worker WebSocket connection
  const {
    connect: connectWorker,
    disconnect: disconnectWorker,
    sendMessage: sendToWorker,
    isConnected: isWorkerConnected,
  } = useWebSocket({
    onMessage: handleWorkerMessage,
  });

  // Check if user has consented
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
  });

  // Get system prompt for current mode
  const systemPrompt = mode ? getPromptForMode(mode) : '';

  // Handle transcript from Gemini - store locally AND send to worker
  const handleTranscript = useCallback((role: 'user' | 'assistant', text: string) => {
    const entry: TranscriptUpdate = {
      speaker: role === 'user' ? 'user' : 'ai',
      text,
      timestamp: Date.now(),
    };

    // Store locally for UI
    addTranscript(entry);

    // Send to worker for metrics calculation
    sendToWorker({
      type: 'TRANSCRIPT_UPDATE',
      payload: entry,
    });
  }, [addTranscript, sendToWorker]);

  // Initialize audio player
  useEffect(() => {
    audioPlayerRef.current = new AudioStreamPlayer({
      onPlaybackStart: () => setIsSpeaking(true),
      onPlaybackEnd: () => setIsSpeaking(false),
      onError: (error) => console.error('Audio playback error:', error),
    });

    return () => {
      audioPlayerRef.current?.dispose();
      audioPlayerRef.current = null;
    };
  }, []);

  // Handle audio from Gemini (queue for smooth playback)
  const handleAudioData = useCallback((data: ArrayBuffer) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.playChunk(data);
    }
  }, []);

  // Handle Gemini error
  const handleGeminiError = useCallback((error: Error) => {
    console.error('Gemini error:', error);
    setGeminiError(error.message);
  }, []);

  // Handle Gemini function calls
  const handleFunctionCall = useCallback((functionCall: GeminiFunctionCall) => {
    // Handle DET phase score reporting
    if (functionCall.name === 'report_det_phase_score') {
      const args = functionCall.args as { phase: DETPhase; score: number; feedback?: string };
      addDETScore({
        phase: args.phase,
        score: args.score,
        feedback: args.feedback,
        overTime: detIsOverTimeRef.current,
      });
      // Don't show overlay for score reporting
      return;
    }

    setActiveFunctionCall(functionCall);
  }, [addDETScore]);

  // Gemini Live connection
  const {
    state: _geminiState,
    isConnecting: isGeminiConnecting,
    isReady: isGeminiReady,
    connect: connectGemini,
    disconnect: disconnectGemini,
    sendAudio: sendAudioToGemini,
    sendClientContent,
  } = useGeminiLive({
    systemPrompt,
    mode: mode || 'conversation',
    onTranscript: handleTranscript,
    onAudioData: handleAudioData,
    onError: handleGeminiError,
    onFunctionCall: handleFunctionCall,
  });

  // Generate context text for phase transitions
  const getPhaseTransitionContext = useCallback((phase: DETPhaseConfig): string => {
    const contexts: Record<DETPhase, string> = {
      read_aloud: `[DET SIMULATION - PHASE 1: READ ALOUD]
Time limit: 30 seconds.
Task: Present a short text for the user to read aloud. Focus on pronunciation and fluency.
After presenting the text, listen to the user read it, then provide brief feedback.
Use the report_det_phase_score function to report the score when you finish evaluating.`,

      short_answer: `[DET SIMULATION - PHASE 2: SHORT ANSWER]
Time limit: 30 seconds.
Task: Ask the user a question that requires a 30-second verbal response.
Listen to their answer, then provide brief feedback.
Use the report_det_phase_score function to report the score when you finish evaluating.`,

      extended_speaking: `[DET SIMULATION - PHASE 3: EXTENDED SPEAKING]
Time limit: 90 seconds.
Task: Present a topic for the user to speak about for 90 seconds.
This should test their ability to speak at length on a topic.
Listen to their response, then provide comprehensive feedback.
Use the report_det_phase_score function to report the score when you finish evaluating.`,
    };
    return contexts[phase.phase];
  }, []);

  // Handle DET phase transitions
  const handleDETPhaseStart = useCallback((phase: DETPhaseConfig) => {
    setDETPhase(phase.phase);
    setState(phase.sessionState);
    setGeminiSpokeThisPhase(false);  // Reset - wait for Gemini to speak for this phase
    // If AI is currently speaking (from previous phase feedback), wait for silence first
    setWaitingForSilence(isSpeaking);

    // Inject context to Gemini
    const context = getPhaseTransitionContext(phase);
    sendClientContent(context);
  }, [setDETPhase, setState, getPhaseTransitionContext, sendClientContent, isSpeaking]);

  const handleDETPhaseEnd = useCallback((phase: DETPhase, overTime: boolean) => {
    // Phase ended - score will come via function call
    console.log(`DET Phase ${phase} ended, overTime: ${overTime}`);
  }, []);

  const handleDETComplete = useCallback(() => {
    setState('FEEDBACK');
    // Send completion context to Gemini
    sendClientContent(`[DET SIMULATION COMPLETE]
All phases have been completed. Please provide a comprehensive summary of the user's performance across all three phases.
Include:
- Overall strengths
- Areas for improvement
- Estimated DET score (10-160 scale)
- Specific recommendations for practice`);
  }, [setState, sendClientContent]);

  // DET Simulation state machine
  const {
    currentPhase: detCurrentPhase,
    countdown: detCountdown,
    isActive: detIsActive,
    isWaitingForTask: detIsWaitingForTask,
    totalPhases: detTotalPhases,
    currentPhaseIndex: detCurrentPhaseIndex,
    isOverTime: detIsOverTime,
    startSimulation: startDETSimulation,
    nextPhase: nextDETPhase,
    stopSimulation: stopDETSimulation,
    startTimer: startDETTimer,
  } = useDETSimulation({
    onPhaseStart: handleDETPhaseStart,
    onPhaseEnd: handleDETPhaseEnd,
    onComplete: handleDETComplete,
  });

  // Keep ref in sync with detIsOverTime for use in callbacks
  useEffect(() => {
    detIsOverTimeRef.current = detIsOverTime;
  }, [detIsOverTime]);

  // Track isSpeaking transitions and manage DET timer state machine
  useEffect(() => {
    const wasSpaking = prevIsSpeakingRef.current;
    const isNowSpeaking = isSpeaking;
    prevIsSpeakingRef.current = isSpeaking;

    // Not in DET waiting mode - skip
    if (!detIsWaitingForTask) return;

    // Transition: speaking → not speaking
    if (wasSpaking && !isNowSpeaking) {
      if (waitingForSilence) {
        // Clear the "waiting for silence" flag - we can now listen for new speech
        setWaitingForSilence(false);
        console.log('[DET] Silence detected, now waiting for new task presentation');
      } else if (geminiSpokeThisPhase) {
        // Gemini finished presenting the task - start timer
        console.log('[DET] Task presentation finished, starting timer');
        const timer = setTimeout(() => {
          startDETTimer();
        }, 300);
        return () => clearTimeout(timer);
      }
    }

    // Transition: not speaking → speaking (new speech started)
    if (!wasSpaking && isNowSpeaking && !waitingForSilence && !geminiSpokeThisPhase) {
      console.log('[DET] New task presentation detected');
      setGeminiSpokeThisPhase(true);
    }
  }, [detIsWaitingForTask, isSpeaking, waitingForSilence, geminiSpokeThisPhase, startDETTimer]);

  // Start session mutation
  const startMutation = useMutation({
    mutationFn: (mode: TrainingMode) => startSession(mode),
    onSuccess: (data) => {
      setSession(data.sessionId, data.wsUrl, data.mode);
      setState('WARMUP');
      setActive(true);
    },
  });

  // End session mutation
  const endMutation = useMutation({
    mutationFn: (id: string) => endSession(id),
    onSuccess: () => {
      clearSession();
      navigate('/dashboard');
    },
  });

  // Audio capture - send to Gemini
  const { isRecording, isMuted, startRecording, stopRecording, toggleMute } = useAudioCapture({
    onChunk: (base64Data) => {
      if (isGeminiReady) {
        // Convert base64 to ArrayBuffer
        const binary = atob(base64Data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        sendAudioToGemini(bytes.buffer);
      }
    },
  });

  // Connect to worker WebSocket when session becomes active
  useEffect(() => {
    if (isActive && wsUrl && sessionId && mode && !isWorkerConnected) {
      // Build full WebSocket URL
      // In production, use VITE_API_URL; in development, use window.location for Vite proxy
      const apiUrl = import.meta.env.VITE_API_URL;
      let fullWsUrl: string;

      if (apiUrl) {
        // Production: convert API URL to WebSocket URL
        const wsProtocol = apiUrl.startsWith('https:') ? 'wss:' : 'ws:';
        const apiHost = new URL(apiUrl).host;
        fullWsUrl = `${wsProtocol}//${apiHost}${wsUrl}`;
      } else {
        // Development: use Vite proxy via window.location
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        fullWsUrl = `${wsProtocol}//${window.location.host}${wsUrl}`;
      }

      connectWorker(fullWsUrl);
    }
  }, [isActive, wsUrl, sessionId, mode, isWorkerConnected, connectWorker]);

  // Send START_SESSION to worker once connected
  useEffect(() => {
    if (isWorkerConnected && sessionId && mode) {
      sendToWorker({
        type: 'START_SESSION',
        payload: {
          mode,
          userId: user?.id || 'anonymous',
          sessionId,
        },
      });
    }
  }, [isWorkerConnected, sessionId, mode, user?.id, sendToWorker]);

  // Connect to Gemini when session becomes active
  useEffect(() => {
    if (isActive && mode && !isGeminiReady && !isGeminiConnecting) {
      connectGemini().catch((error) => {
        console.error('Failed to connect to Gemini:', error);
        setGeminiError(error.message);
      });
    }
  }, [isActive, mode, isGeminiReady, isGeminiConnecting, connectGemini]);

  // Track when Gemini has spoken at least once
  useEffect(() => {
    if (isSpeaking && !geminiHasSpoken) {
      setGeminiHasSpoken(true);
    }
  }, [isSpeaking, geminiHasSpoken]);

  // Start DET simulation when mode is duolingo_test and Gemini finishes speaking
  useEffect(() => {
    // Only start after Gemini has spoken once AND has stopped speaking
    if (mode === 'duolingo_test' && isGeminiReady && !detIsActive && state === 'WARMUP' && geminiHasSpoken && !isSpeaking) {
      // Start simulation after Gemini finishes its initial greeting
      const timer = setTimeout(() => {
        startDETSimulation();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [mode, isGeminiReady, detIsActive, state, isSpeaking, geminiHasSpoken, startDETSimulation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectGemini();
      disconnectWorker();
      audioPlayerRef.current?.dispose();
      stopDETSimulation();
      resetDETState();
    };
  }, [disconnectGemini, disconnectWorker, stopDETSimulation, resetDETState]);

  // Handle mode selection
  const handleModeSelect = (selectedMode: TrainingMode) => {
    if (user && user.hasConsented === false) {
      setPendingMode(selectedMode);
      setShowConsent(true);
    } else {
      startMutation.mutate(selectedMode);
    }
  };

  // Handle consent acceptance
  const handleConsentAccept = () => {
    setShowConsent(false);
    if (pendingMode) {
      startMutation.mutate(pendingMode);
      setPendingMode(null);
    }
  };

  // Handle consent decline
  const handleConsentDecline = () => {
    setShowConsent(false);
    setPendingMode(null);
  };

  // Handle session end
  const handleEndSession = () => {
    setShowEndConfirm(true);
  };

  const handleConfirmEnd = async () => {
    setShowEndConfirm(false);
    if (sessionId) {
      stopRecording();
      disconnectGemini();
      audioPlayerRef.current?.stop();

      // The REST API handles ending the session in the Durable Object
      // We just need to disconnect cleanly after the API call completes
      try {
        await endMutation.mutateAsync(sessionId);
      } finally {
        // Disconnect after the session is ended on the server
        disconnectWorker();
      }
    }
  };

  // Show mode selector if no active session
  if (!isActive && !startMutation.isPending && !sessionId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-900">Start Training</h1>
            <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <ModeSelector onSelect={handleModeSelect} />
        </main>

        {showConsent && (
          <ConsentModal onAccept={handleConsentAccept} onDecline={handleConsentDecline} />
        )}
      </div>
    );
  }

  // Active session UI
  return (
    <div className="min-h-screen bg-gray-900 text-white safe-area-inset-y" data-testid="session-active">
      {/* Header */}
      <header className="border-b border-gray-800 px-3 sm:px-4 py-2 sm:py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <span data-testid="current-mode" className="font-medium text-sm sm:text-base truncate">
              {mode ? TRAINING_MODE_LABELS[mode] : 'Session'}
            </span>
            <span data-testid="session-state" className="text-xs sm:text-sm text-gray-400 hidden sm:inline">
              {state}
            </span>
            {/* Gemini connection status */}
            <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap ${
              isGeminiReady ? 'bg-green-600' :
              isGeminiConnecting ? 'bg-yellow-600' : 'bg-red-600'
            }`}>
              {isGeminiReady ? 'Connected' :
               isGeminiConnecting ? 'Connecting' : 'Offline'}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <SessionTimer />
            <button
              data-testid="end-session-button"
              onClick={handleEndSession}
              className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 active:bg-red-800 rounded text-xs sm:text-sm touch-target"
            >
              End
            </button>
          </div>
        </div>

        {/* Phase Progress */}
        <div data-testid="phase-progress" className="mt-2">
          <PhaseProgress currentState={state} />
        </div>
      </header>

      {/* Function Call Overlay */}
      <FunctionCallOverlay
        functionCall={activeFunctionCall}
        onDismiss={() => setActiveFunctionCall(null)}
        isSpeaking={isSpeaking}
      />

      {/* Gemini Error Banner */}
      {geminiError && (
        <div className="bg-red-900 text-red-100 px-4 py-2 text-sm">
          Gemini Error: {geminiError}
          <button
            onClick={() => {
              setGeminiError(null);
              connectGemini();
            }}
            className="ml-4 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-32 sm:pb-28 safe-area-inset-x">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Transcript Panel */}
          <div className="lg:col-span-2 order-1">
            <TranscriptPanel entries={transcript} />
          </div>

          {/* Feedback Panel - on mobile shows below transcript */}
          <div className="order-2 lg:order-none">
            {/* DET Phase Control - show for duolingo_test mode */}
            {mode === 'duolingo_test' && detIsActive && detCurrentPhase && (
              <div className="mb-3 sm:mb-4">
                <DETPhaseControl
                  currentPhase={detCurrentPhase}
                  countdown={detCountdown}
                  totalPhases={detTotalPhases}
                  currentPhaseIndex={detCurrentPhaseIndex}
                  isOverTime={detIsOverTime}
                  isWaitingForTask={detIsWaitingForTask}
                  onNextPhase={nextDETPhase}
                  disabled={!isGeminiReady}
                />
              </div>
            )}

            <FeedbackPanel mode={mode} />

            {/* Structure Guide for Technical Explanation */}
            {mode === 'technical_explanation' && (
              <div data-testid="structure-guide" className="mt-3 sm:mt-4 card bg-gray-800 p-3 sm:p-4">
                <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-2">Structure Guide</h3>
                <ul className="space-y-1 text-xs sm:text-sm">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-600"></span>
                    Context ({METRICS_CONFIG.STRUCTURE_WEIGHTS.intro} pts)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-600"></span>
                    Problem ({METRICS_CONFIG.STRUCTURE_WEIGHTS.problem} pts)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-600"></span>
                    Solution ({METRICS_CONFIG.STRUCTURE_WEIGHTS.solution} pts)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-600"></span>
                    Trade-offs ({METRICS_CONFIG.STRUCTURE_WEIGHTS.tradeoffs} pts)
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mic Control */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-3 sm:p-4 safe-area-inset-bottom">
        <div className="container mx-auto flex items-center justify-center gap-3 sm:gap-4">
          <MicControl
            isRecording={isRecording}
            isMuted={isMuted}
            onStart={startRecording}
            onStop={stopRecording}
            onToggleMute={toggleMute}
            disabled={!isGeminiReady}
          />

          {/* Recording and speaking indicators - stacked on mobile */}
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
            {isRecording && (
              <span data-testid="recording-active-indicator" className="text-[10px] sm:text-xs text-red-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500 animate-pulse"></span>
                Recording
              </span>
            )}

            {isSpeaking && (
              <span data-testid="ai-speaking-indicator" className="text-[10px] sm:text-xs text-blue-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-blue-500 animate-pulse"></span>
                AI Speaking
              </span>
            )}
          </div>
        </div>
      </footer>

      {/* End Confirmation Dialog */}
      {showEndConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="end-session-confirm">
          <div className="bg-gray-800 rounded-xl p-6 max-w-sm mx-4">
            <h2 className="text-lg font-semibold mb-2">End Session?</h2>
            <p className="text-gray-400 mb-4">Your progress will be saved.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-end-session"
                onClick={handleConfirmEnd}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Complete */}
      {state === 'COMPLETE' && (
        <div data-testid="session-summary" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Session Complete!</h2>
            <p className="text-gray-400 mb-6">Great work! Your results are being processed.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 rounded-lg"
            >
              View Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
